import React from 'react';
import { Box, Stack, Typography, Paper, Grid, LinearProgress, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/**
 * Displays the allocation preview per agent.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.allocateDetails - The allocation details object containing userAllocatedCountMap.
 * @param {Object} props.shift - The shift object containing the allocation limit per user.
 * @returns {JSX.Element}
 */
const AgentPreviewDisplay = ({ allocateDetails, shift }) => {
  if (!allocateDetails || !allocateDetails.userAllocatedCountMap || !shift) {
    return (
      <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Agent allocation data is not available.
        </Typography>
      </Paper>
    );
  }

  const { userAllocatedCountMap } = allocateDetails;
  const agentLimit = shift.number || 0; // Allocation limit per agent from the shift

  // Filter out entries without a user object (might happen if data is inconsistent)
  const validAgentEntries = Object.entries(userAllocatedCountMap).filter(
    ([_, data]) => data.user
  );

  if (validAgentEntries.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No agents found in the allocation preview.
        </Typography>
      </Paper>
    );
  }


  return (
    <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 0, mr: 1 }}>
          Agent Allocation Preview
        </Typography>
        <Tooltip title={
          <React.Fragment>
            <Typography color="inherit">Color Info:</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <Box sx={{ width: 10, height: 10, bgcolor: 'success.light', mr: 1, border: '1px solid grey' }} /> Agent can be assigned cases.
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <Box sx={{ width: 10, height: 10, bgcolor: 'error.light', mr: 1, border: '1px solid grey' }} /> Agent cannot be assigned (0 new cases).
            </Box>
          </React.Fragment>
        }>
          <InfoOutlinedIcon fontSize="small" color="action" />
        </Tooltip>
      </Box>
      <Grid container spacing={2}>
        {validAgentEntries.map(([userId, data]) => {
          const agent = data.user;
          const currentAllocated = data.prevCount || 0; // Assuming 'allocated' exists on user object
          const newCasesCount = data.newCaseCount || 0;
          const totalAfterAssign = currentAllocated + newCasesCount;
          const progress = agentLimit > 0 ? (totalAfterAssign / agentLimit) * 100 : 0;
          const overLimit = totalAfterAssign > agentLimit;


          return (
            <Grid item xs={12} sm={6} md={4} key={userId}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderColor: newCasesCount === 0 ? 'error.light' : 'success.light',
                  borderWidth: 2, // Make border slightly thicker
                  boxShadow: newCasesCount === 0 ? '0 0 5px rgba(255, 0, 0, 0.5)' : 'none', // Reddish shadow for zero cases
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                  {agent.name} ({agent.uuid || userId})
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    Current: {currentAllocated}
                  </Typography>
                  <Typography variant="body2">
                    New: {newCasesCount}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: overLimit ? 'error.main' : 'inherit' }}>
                    Total: {totalAfterAssign} / {agentLimit}
                  </Typography>
                </Stack>
                <Tooltip title={`Allocated: ${totalAfterAssign} / Limit: ${agentLimit}`}>
                  <Box sx={{ mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(progress, 100)} // Cap progress at 100% visually
                      color={overLimit ? 'error' : 'primary'}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                </Tooltip>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Paper>
  );
};

export default AgentPreviewDisplay;